import {
  Alert,
  Box,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { MultiValue } from 'react-select';

import { SourceWhaleTrackingEvents, userTracking } from '@revelio/core';
import type { TalentDiscoveryV2RespUser } from '@revelio/data-access';
import {
  Button,
  ButtonGroup,
  CheckboxIndicator,
  Label,
  Select,
} from '@revelio/ui';

import sourceWhaleCampaignsIcon from '../../../../../../images/icons/sourcewhale-campaigns-icon.svg';
import sourceWhaleContactsIcon from '../../../../../../images/icons/sourcewhale-contacts-icon.svg';
import sourceWhaleIcon from '../../../../assets/sourcewhale-icon.png';
import themedModalStyles from '../../../Modal/themedModal.module.css';
import {
  CannotFetchCampaignsError,
  useSourceWhaleCampaigns,
} from '../hooks/use-source-whale-campaigns';
import { useSourceWhaleCandidates } from '../hooks/use-source-whale-candidates';
import {
  CannotFetchProjectsError,
  useSourceWhaleProjects,
} from '../hooks/use-source-whale-projects';
import { mapTDUsersToSWCandidates } from '../source-whale.model';
import { SelectableCard } from './selectable-card';
import styles from './source-whale-modals.module.css';

const MAX_CAMPAIGN_CANDIDATES = 50;

enum ExportMode {
  Contacts = 'contacts',
  Campaign = 'campaign',
}

type Option = {
  label: string;
  value: string;
};

interface SourceWhaleExportModalProps {
  apiKey: string;
  isOpen: boolean;
  selectedUsers: TalentDiscoveryV2RespUser[];
  onClose: () => void;
  onEditApiKey: () => void;
}

const modalStyles = {
  header: {
    position: 'relative',
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: '23px',
    color: 'text.primary',
  },
  alert: {
    backgroundColor: 'var(--modal-error-bg, #FDF0F3)',
    fontSize: '10px',
    lineHeight: '11px',
    fontWeight: '400',
    color: 'var(--modal-error-text, #E76A87)',
    borderRadius: 'sm',
    mb: 2,
  },
  alertLink: {
    color: 'inherit',
    fontWeight: '600',
    cursor: 'pointer',
    _hover: {
      textDecoration: 'underline',
    },
  },
} as const;

export const SourceWhaleExportModal = ({
  apiKey,
  isOpen,
  onClose,
  selectedUsers,
  onEditApiKey,
}: SourceWhaleExportModalProps) => {
  const isCampaignDisabled = selectedUsers.length > MAX_CAMPAIGN_CANDIDATES;

  const [exportMode, setExportMode] = useState<ExportMode | null>(
    isCampaignDisabled ? ExportMode.Contacts : null
  );
  const [campaign, setCampaign] = useState<Option | null>(null);
  const [error, setError] = useState<string>('');

  const { data, isLoading, isError } = useSourceWhaleCampaigns({
    apiKey,
    onError: (error) => setError(error),
    enabled: isOpen,
  });

  const { data: projectsData, isLoading: isLoadingProjects } =
    useSourceWhaleProjects({
      apiKey,
      onError: (error) => setError(error),
      enabled: isOpen,
    });

  const { mutate: addCandidates, isPending: isAddingCandidates } =
    useSourceWhaleCandidates({
      onSuccess: () => {
        handleClose();
      },
      onError: (error) => {
        setError(error);
      },
    });

  const campaignOptions = useMemo(() => {
    if (isError || !data) return [];

    return (
      data?.campaigns.campaigns.map((campaign) => ({
        label: campaign.campaignName,
        value: campaign.campaignId,
      })) ?? []
    );
  }, [data, isError]);

  const projectSelectContainerRef = useRef<HTMLDivElement>(null);
  const [isSyncWithCrmSelected, setIsSyncWithCrmSelected] = useState(false);

  const handleSyncWithCrmToggle = () => {
    const next = !isSyncWithCrmSelected;
    setIsSyncWithCrmSelected(next);
    if (next) {
      // Focus the project select once it re-renders enabled; with
      // `openMenuOnFocus` this also opens its menu.
      requestAnimationFrame(() => {
        projectSelectContainerRef.current?.querySelector('input')?.focus();
      });
    }
  };

  const [projects, setProjects] = useState<MultiValue<Option> | null>(null);
  const projectOptions =
    projectsData?.projects?.map((project) => ({
      label: project.projectName,
      value: project.projectId,
    })) ?? [];

  const projectIds = (() => {
    if (!projects || projects.length === 0) return undefined;
    return projects.map((project) => project.value);
  })();

  const isSubmitDisabled =
    !exportMode || (exportMode === ExportMode.Campaign && !campaign);

  const handleSubmit = useCallback(() => {
    if (!apiKey) return;

    if (exportMode === ExportMode.Campaign) {
      userTracking.track(SourceWhaleTrackingEvents.EXPORT_CAMPAIGN, {
        candidatesCount: selectedUsers.length,
      });
    } else if (exportMode === ExportMode.Contacts) {
      userTracking.track(SourceWhaleTrackingEvents.EXPORT_CONTACTS, {
        contactsCount: selectedUsers.length,
        syncWithCrm: isSyncWithCrmSelected,
        projectsCount: projects?.length || 0,
      });
    }

    addCandidates({
      apiKey,
      candidates: mapTDUsersToSWCandidates({ users: selectedUsers }),
      campaignId:
        exportMode === ExportMode.Campaign ? campaign?.value : undefined,
      projectIds:
        exportMode === ExportMode.Contacts && isSyncWithCrmSelected
          ? projectIds
          : undefined,
    });
  }, [
    apiKey,
    addCandidates,
    selectedUsers,
    exportMode,
    campaign,
    projectIds,
    isSyncWithCrmSelected,
    projects,
  ]);

  const handleReset = useCallback(() => {
    setError('');
    setExportMode(isCampaignDisabled ? ExportMode.Contacts : null);
    setCampaign(null);
  }, [isCampaignDisabled]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  return (
    <Modal
      id="source-whale-export-modal"
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="sm"
    >
      <ModalOverlay />

      <ModalContent
        className={themedModalStyles.content}
        userSelect="none"
        w="291px"
      >
        <ModalHeader sx={modalStyles.header}>
          <Flex gap="2" alignItems="center">
            <Image src={sourceWhaleIcon} boxSize={4} />
            SourceWhale Export
          </Flex>
          <ModalCloseButton size="sm" top="50%" transform="translateY(-50%)" />
        </ModalHeader>

        <ModalBody py={0}>
          {error && (
            <ExportErrorAlert
              error={error}
              onEditApiKey={onEditApiKey}
              onClose={handleClose}
            />
          )}

          <Label as="p" size="sm" className={styles.sectionLabel}>
            Where would you like to export to?
          </Label>

          <VStack w="full" spacing={2} mb={2}>
            <SelectableCard
              icon={<Image src={sourceWhaleContactsIcon} />}
              label="Add to contacts"
              isSelected={exportMode === ExportMode.Contacts}
              onClick={() => setExportMode(ExportMode.Contacts)}
            />
            {exportMode === ExportMode.Contacts && (
              <Box width="100%" mt={1}>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    className={styles.checkboxInput}
                    checked={isSyncWithCrmSelected}
                    onChange={handleSyncWithCrmToggle}
                  />
                  <CheckboxIndicator checked={isSyncWithCrmSelected} />
                  <Label as="span" size="sm">
                    Sync with CRM & ATS?
                  </Label>
                </label>

                <Box ref={projectSelectContainerRef}>
                  <Select
                    isMulti
                    isDisabled={!isSyncWithCrmSelected}
                    options={projectOptions}
                    hideSelectedOptions={false}
                    value={projects}
                    onChange={(options) => setProjects(options)}
                    placeholder="Select CRM & ATS"
                    isLoading={isLoadingProjects}
                    size="sm"
                    isClearable={false}
                    closeMenuOnSelect={false}
                    openMenuOnFocus
                  />
                </Box>
              </Box>
            )}
            <SelectableCard
              icon={<Image src={sourceWhaleCampaignsIcon} />}
              label="Add to a campaign"
              tooltip="Select up to 50 candidates to add to your campaign"
              isSelected={exportMode === ExportMode.Campaign}
              isDisabled={isCampaignDisabled}
              onClick={() =>
                !isCampaignDisabled && setExportMode(ExportMode.Campaign)
              }
            />
          </VStack>

          {exportMode === ExportMode.Campaign && (
            <>
              <Label as="p" size="sm" className={styles.campaignLabel}>
                Choose a campaign
              </Label>

              <Select<Option>
                options={campaignOptions}
                value={campaign}
                onChange={(option) => setCampaign(option)}
                placeholder="Select a campaign"
                isLoading={isLoading}
                size="sm"
              />
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup size="sm">
            <Button variant="link" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              loading={isAddingCandidates}
              data-testid="Export-submit"
            >
              Export
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ExportErrorAlert = ({
  error,
  onEditApiKey,
  onClose,
}: {
  error: string;
  onEditApiKey: () => void;
  onClose: () => void;
}) => {
  const renderErrorRecoveryAction = () => {
    // Failing to fetch campaigns or projects often indicate an invalid API key.
    // Provide a direct way for users to update their key.
    if (
      error === CannotFetchCampaignsError ||
      error === CannotFetchProjectsError
    ) {
      return (
        <>
          {' '}
          Please{' '}
          <Text
            as="span"
            sx={modalStyles.alertLink}
            onClick={() => {
              onClose();
              onEditApiKey();
            }}
          >
            check your API key
          </Text>{' '}
          and try again.
        </>
      );
    }
    return null;
  };

  return (
    <Alert status="error" sx={modalStyles.alert}>
      <span>
        {error}
        {renderErrorRecoveryAction()}
      </span>
    </Alert>
  );
};
